/**
 * French render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'fr')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const frenchLexicon: LanguageLexicon = {
  events: {
    blur: { primary: 'flou' },
    change: { primary: 'changer' },
    click: { primary: 'clic' },
    dblclick: { primary: 'doubleclic' },
    focus: { primary: 'focus' },
    input: { primary: 'saisie' },
    keydown: { primary: 'touche bas' },
    keypress: { primary: 'touchepressée' },
    keyup: { primary: 'touche haut' },
    load: { primary: 'charger' },
    mousedown: { primary: 'mousedown' },
    mouseenter: { primary: 'sourisentrer' },
    mouseleave: { primary: 'sourissortir' },
    mousemove: { primary: 'sourisbouger' },
    mouseout: { primary: 'souris dehors' },
    mouseover: { primary: 'souris dessus' },
    mouseup: { primary: 'mouseup' },
    reset: { primary: 'réinitialiser' },
    resize: { primary: 'redimensionner' },
    scroll: { primary: 'défiler' },
    submit: { primary: 'soumettre' },
    touchcancel: { primary: 'toucherannuler' },
    touchend: { primary: 'toucherfin' },
    touchmove: { primary: 'toucherbouger' },
    touchstart: { primary: 'touchercommencer' },
    unload: { primary: 'décharger' },
  },
  logical: {
    and: { primary: 'et' },
    changes: { primary: 'change' },
    contains: { primary: 'contient' },
    else: { primary: 'sinon' },
    end: { primary: 'fin' },
    equals: { primary: 'égale' },
    exists: { primary: 'existe' },
    has: { primary: 'a' },
    have: { primary: 'ai' },
    includes: { primary: 'inclut' },
    is: { primary: 'est' },
    live: { primary: 'vif' },
    matches: { primary: 'correspond' },
    not: { primary: 'non' },
    or: { primary: 'ou' },
    otherwise: { primary: 'autrement' },
    then: { primary: 'alors' },
    when: { primary: 'quand' },
    where: { primary: 'où' },
  },
  temporal: {
    h: { primary: 'h' },
    hour: { primary: 'heure' },
    hours: { primary: 'heures' },
    millisecond: { primary: 'milliseconde' },
    milliseconds: { primary: 'millisecondes' },
    min: { primary: 'min' },
    minute: { primary: 'minute' },
    minutes: { primary: 'minutes' },
    ms: { primary: 'ms' },
    s: { primary: 's' },
    second: { primary: 'seconde' },
    seconds: { primary: 'secondes' },
    times: { primary: 'fois' },
  },
  values: {
    body: { primary: 'corps' },
    detail: { primary: 'détail' },
    document: { primary: 'document' },
    element: { primary: 'élément' },
    event: { primary: 'événement' },
    false: { primary: 'faux' },
    it: { primary: 'ça' },
    its: { primary: 'son' },
    me: { primary: 'moi' },
    my: { primary: 'mon' },
    myself: { primary: 'moi-même' },
    null: { primary: 'nul' },
    result: { primary: 'résultat' },
    target: { primary: 'cible' },
    true: { primary: 'vrai' },
    undefined: { primary: 'indéfini' },
    value: { primary: 'valeur' },
    window: { primary: 'fenêtre' },
    you: { primary: 'tu' },
    your: { primary: 'ton' },
    yourself: { primary: 'toi-même' },
  },
  attributes: {
    attribute: { primary: 'attribut' },
    attributes: { primary: 'attributs' },
    class: { primary: 'classe' },
    classes: { primary: 'classes' },
    properties: { primary: 'propriétés' },
    property: { primary: 'propriété' },
    style: { primary: 'style' },
    styles: { primary: 'styles' },
  },
  expressions: {
    at: { primary: 'à' },
    characters: { primary: 'caractères' },
    children: { primary: 'enfants' },
    closest: { primary: 'proche' },
    empty: { primary: 'vide' },
    'ends with': { primary: 'finit par' },
    exclusive: { primary: 'exclusif' },
    first: { primary: 'premier' },
    'ignoring case': { primary: 'en ignorant la casse' },
    inclusive: { primary: 'inclusif' },
    'joined by': { primary: 'joint par' },
    last: { primary: 'dernier' },
    'mapped to': { primary: 'transformé en' },
    next: { primary: 'suivant' },
    no: { primary: 'aucun' },
    parent: { primary: 'parent' },
    prev: { primary: 'préc' },
    previous: { primary: 'précédent' },
    random: { primary: 'aléatoire' },
    some: { primary: 'quelques' },
    'sorted by': { primary: 'trié par' },
    'split by': { primary: 'divisé par' },
    'starts with': { primary: 'commence par' },
    within: { primary: 'dans' },
  },
};

registerLexicon('fr', frenchLexicon);
